// Sidebar has 3 states : maximized (default), minimized, hovered
<template>
  <div :class="{ 'is-minimized': isMinimized }" class="sidebar" ref="sidebar">
    <div class="sidebar__backup"></div>

    <div class="sidebar__container" @mouseenter="showDrawer()" @mouseleave="hideDrawer()">
      <div class="sidebar__desktop-menu">
        <div class="sidebar__logo" @click="backHome">
          <SmallLogo class="sidebar__smallLogo" />
          <LargeLogo class="sidebar__largeLogo" />
        </div>
        <div v-if="showSidebarToggle" class="sidebar__toggle" @mousedown.stop="handleToggle">
          <BaseRoundButton
            icon="sidebar"
            :size="32"
            is-transparent
            :tooltip-text="sidebarToggleTooltip"
            tooltip-placement="bottom"
          />
        </div>
      </div>

      <div class="sidebar__content">
        <div class="sidebar__menu" @click="isMobile ? handleToggle() : ''">
          <SidebarMenu :minimized="isMinimized ? isHovered : isMinimized" ref="sidebarMenu" />
        </div>

        <div class="sidebar__footer">
          <SidebarConfig />
        </div>
      </div>
    </div>

    <div class="sidebar__overlay" v-if="!isMinimized" @click="handleToggle"></div>
  </div>
</template>

<script>
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import LargeLogo from '@/assets/images/aloha-logo.svg';
import SmallLogo from '@/assets/images/aloha-logomark.svg';
import SidebarConfig from '@/components/layout/SidebarConfig';
import SidebarMenu from '@/components/layout/SidebarMenu';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import { closeOnBack } from '@/util/back-button';
import { paths } from '@/util/paths';
import { viewport } from '@/util/viewport';

export default {
  name: 'Sidebar',
  mixins: [IsMobileMixin],
  components: {
    LargeLogo,
    SmallLogo,
    BaseRoundButton,
    SidebarConfig,
    SidebarMenu,
  },
  props: {
    // value is needed to use `v-model`
    value: Boolean,
  },
  data() {
    return {
      isHovered: true,
    };
  },
  computed: {
    isMinimized() {
      return this.value;
    },
    showSidebarToggle() {
      return !this.isMinimized || !this.isHovered;
    },
    sidebarToggleTooltip() {
      return this.isMinimized ? this.$t('sidebar.expand') : this.$t('sidebar.collapse');
    },
  },
  watch: {
    // Open over the page, the menu closes on back.
    isMinimized(minimized) {
      if (this.releaseBack) this.releaseBack();
      this.releaseBack = !minimized && this.isMobile ? closeOnBack(() => this.handleToggle()) : null;
    },
  },
  methods: {
    backHome() {
      if (this.$route.name !== 'tenders') {
        this.$router.push(paths.root);
      }
    },
    handleToggle() {
      const el = this.$refs.sidebarMenu.$el;
      if (this.isMinimized) {
        el.classList.remove('is-minimized'); // Force the sidebarMenu to open
      } else {
        el.classList.add('is-minimized'); // Force the sidebarMenu to close
      }
      this.$emit('input', !this.isMinimized);
    },
    showDrawer() {
      // A tap fires mouseenter too, and nothing fires mouseleave after it, so on a touch
      // screen the menu would open over the page on every tap and stay open.
      if (this.isMinimized && viewport.canHover) {
        this.$refs.sidebar.classList.add('is-hovered');
        this.$refs.sidebarMenu.$el.classList.remove('is-minimized');

        this.isHovered = false;
      }
    },
    hideDrawer() {
      if (this.isMinimized) {
        this.$refs.sidebar.classList.remove('is-hovered');

        this.isHovered = true;
      }
    },
  },
};
</script>

<style lang="scss">
// Basic status of Sidebar.vue is maximized
.sidebar {
  background: $color-white;
  z-index: $side-bar-index;

  &__backup {
    display: flex;
    width: $sidebar-opened-width;
    transition: width 200ms ease-in-out;
  }

  &__container {
    height: var(--viewport-height);
    width: $sidebar-opened-width;
    transition: width 200ms ease-in-out; //opening main
  }

  &__desktop-menu {
    height: $menu-top-bar-height;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    border-right: 1px solid $color-border;
    width: 100%;
    transition: width 200ms ease-in-out; //opening
  }

  &__logo {
    flex: 1;
    height: 100%;
    position: relative;
    cursor: pointer;
    min-width: 0;
  }

  &__toggle {
    flex-shrink: 0;
    margin-right: 12px;
  }

  &__content {
    height: calc(100% - 56px);
    display: flex;
    flex-direction: column;
    border-right: 1px solid $color-border;
    width: 100%;
    transition: width 200ms ease-in-out; //opening
    overflow-y: auto;
    overflow-x: hidden !important;
  }

  $sidebar-logo-size: 24px;

  &__smallLogo {
    width: $sidebar-logo-size;
    height: $sidebar-logo-size;
    position: absolute;
    top: 50%;
    left: 22px;
    transform: translateY(-50%);
    z-index: 1;
  }

  &__largeLogo {
    color: $color-brand-500;
    height: $sidebar-logo-size;
    width: auto;
    position: absolute;
    top: 50%;
    left: 22px;
    transform: translateY(-50%);
    display: flex;
    flex-shrink: 0;
    // Keep icon size identical; reveal only the wordmark to the right.
    clip-path: inset(0 0 0 #{$sidebar-logo-size});
    transition: clip-path 200ms ease-in-out 100ms;
  }

  &__footer {
    margin-top: auto;
    position: sticky;
    bottom: 0;
    background: $color-white;
  }

  &__overlay {
    display: none;
  }

  &.is-minimized {
    .sidebar {
      &__backup {
        width: $sidebar-closed-width;
      }

      &__container {
        width: $sidebar-closed-width;
        position: absolute;
        height: var(--viewport-height);
        background-color: $color-white;
        transition: all 200ms ease-in-out; //closing
      }

      &__desktop-menu,
      &__content {
        width: 100%;
        transition: width 200ms ease-in-out; //closing
        overflow-y: hidden;
      }

      &__largeLogo {
        clip-path: inset(0 100% 0 0);
        transition: clip-path 150ms ease-in-out;
      }
    }
  }
  &.is-hovered {
    .sidebar {
      &__container {
        width: $sidebar-opened-width;
        box-shadow: $shadow-default;
        transition: width 200ms ease-in-out 100ms;
      }

      &__desktop-menu,
      &__content {
        width: 100%;
        transition: width 200ms ease-in-out 100ms;
        overflow-y: auto;
      }

      &__largeLogo {
        clip-path: inset(0 0 0 #{$sidebar-logo-size});
        transition: clip-path 200ms ease-in-out 100ms;
      }
    }
  }
}
@media screen and (max-width: 1080px) {
  // Mobile sidebar opened
  .sidebar {
    &__backup {
      display: none;
    }

    &__container {
      height: calc(var(--viewport-height) - 56px);
      top: 56px;
      position: absolute;
      z-index: $side-bar-index;
      transition: all 200ms ease-in-out; //opening content
      background: $color-white;
    }

    &__desktop-menu {
      display: none;
    }

    &__content {
      width: 100%;
      position: absolute;
      box-shadow: $shadow-default;
      height: 100%;
      transition: all 150ms ease-in-out; //opening content
    }

    &__overlay {
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: $top-index;
      top: 56px;
    }

    // Mobile sidebar closed
    &.is-minimized {
      .sidebar {
        &__container {
          width: 0px;
          transition: all 100ms ease-in-out; //closing main
        }

        &__content {
          visibility: hidden;
          opacity: 0;
          transition: all 100ms ease-in-out; //closing content
        }
      }
    }
  }
}
@media screen and (max-height: 460px) {
  .sidebar {
    &__footer {
      border-top: 1px solid $color-border;
    }
  }
}
</style>
